/**
 * SheetDialog.tsx
 *
 * The current student/parent BottomSheet, generalised: bottom sheet through
 * `md`, the same component centres as a dialog from `lg` (docs/DESIGN.md
 * "Bottom sheet on phone and tablet, dialog from `lg`"). `wide` widens the
 * desktop dialog (`lg:max-w-2xl`); `tall` keeps the ~90%-tall panel on phone
 * and adds an `lg:h-[85vh]` desktop equivalent, since a fixed vh doesn't
 * apply to a bottom-anchored phone sheet the same way.
 *
 * No icon dependency: the desktop close control is drawn as an inline SVG
 * (Tabler's 24px/1.8-stroke X) rather than importing `@tabler/icons-react`,
 * which this package must never depend on.
 */

import type { ReactNode } from 'react';
import { useDialogFocus } from './useDialogFocus';

interface SheetDialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  /** ~90% tall panel on phone (`h-11/12`), `lg:h-[85vh]` from `lg` — chat
   *  surfaces need more vertical room than a confirmation or short list. */
  tall?: boolean;
  /** Children own their full layout — a header row (with its own close
   *  control; the desktop X is skipped), a scrolling body, a
   *  pinned footer — so the shell adds no padding of its own. Default
   *  sheets keep the padded, whole-sheet-scrolls wrapper. */
  bare?: boolean;
  /** Widen the desktop dialog to `lg:max-w-2xl` instead of `max-w-md`. */
  wide?: boolean;
  closeLabel: string;
}

export default function SheetDialog({
  open,
  onClose,
  children,
  ariaLabel,
  tall,
  bare,
  wide,
  closeLabel,
}: SheetDialogProps) {
  const panelRef = useDialogFocus(open, onClose);

  if (!open) return null;

  return (
    // z-60, above the rest of the chrome — the highest everyday chrome.
    <div className="fixed inset-0 z-60 flex items-end justify-center lg:items-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`relative w-full max-w-md mx-auto bg-surface rounded-t-lg flex flex-col sheet-up safe-area lg:rounded-lg lg:shadow-xl lg:safe-area-none outline-none ${
          tall ? 'h-11/12 lg:h-[85vh]' : 'max-h-[85vh]'
        } ${wide ? 'lg:max-w-2xl' : ''}`}
      >
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0 lg:hidden">
          <div className="w-10 h-1.5 rounded-pill bg-border" />
        </div>

        {/* Desktop close control, in flow above the body (not absolute) so a
            sheet whose first row already ends in a control — the Nut Shop's
            balance pill — is not covered by it. Not on `bare` sheets: those
            own their whole layout, header row and close button included
            (AskSheet), and a second X in the same corner would double it. */}
        {!bare && (
          <div className="hidden lg:flex flex-shrink-0 justify-end px-3 pt-3 -mb-2">
            <button
              onClick={onClose}
              aria-label={closeLabel}
              className="flex size-11 items-center justify-center rounded-pill text-muted hover:bg-bg"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {bare ? (
          <div className="flex flex-1 min-h-0 flex-col">{children}</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 pb-6">{children}</div>
        )}
      </div>
    </div>
  );
}
